import React, { useContext, useEffect, Dispatch, SetStateAction, useState, useRef, Fragment } from 'react';
import classnames from 'classnames';
import cloneDeep from 'lodash.clonedeep';
import Erasure from 'sunzi-plugin-erasure';
import { Header, Icon } from '@/components';
import { i18n, loadHTMLImage } from '@/utils';
import { ProductContext, SockTaskStatus } from '.';
import styles from './style.less';

interface EditProps {
  /** 当前修改的图片 */
  currentIndex: number;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onConfirm: () => void;
  /** 设置 zuploader 插件显影函数 */
  setZuploaderVisible: Dispatch<SetStateAction<boolean>>;
  /** 谷歌统计 */
  gtag: any;
}

const Edit: React.FC<EditProps> = ({ currentIndex, onClose, onConfirm, setZuploaderVisible, gtag }) => {

  const _canvas = useRef<HTMLCanvasElement>(null);
  /** 橡皮檫组件实例 */
  const _erasure = useRef<Erasure>();
  // 获取袜子相关 context
  const { images, setImages, setTaskStatus } = useContext(ProductContext);
  /** 橡皮檫显隐 */
  const [ erasureVisible, setErasureVisible ] = useState<boolean>(false);
  /** 橡皮檫撤回状态 */
  const [ revokeDisabled, setRevokeDisabled ] = useState<boolean>(true);
  /** 修改的图片 */
  const [ currentImage, setCurrentImage ] = useState<string>(images[currentIndex].image);

  useEffect(() => {
    const canvas = _canvas.current;
    if (canvas) {
      const { offsetWidth, offsetHeight } = canvas;
      const scale = Math.min(1000 / offsetWidth, 1000 / offsetHeight);
      canvas.width = offsetWidth * scale;
      canvas.height = offsetHeight * scale;
      loadHTMLImage(images[currentIndex].image).then(image => {
        _erasure.current = new Erasure(canvas, image, {
          padding: 60,
          color: 'rgba(255, 0, 0, 0.5)',
          onStackChange: (stack) => {
            if (stack.length === 0 && !revokeDisabled)
              setRevokeDisabled(true);
            else if (stack.length > 0 && revokeDisabled)
              setRevokeDisabled(false);
          }
        });
        _erasure.current.modeToggle();
      });
    }
  }, []);

  /** 橡皮擦显隐 */
  const handleErasureVisible = (visible: boolean) => {
    setErasureVisible(visible);
    _erasure.current?.modeToggle();
    if (visible) gtag('event', 'event_erasure_open');
    else gtag('event', 'event_erasure_close');
  }

  /** 删除图片 */
  const handleDelete = () => {
    images.splice(currentIndex, 1);
    setImages(cloneDeep(images));
    onClose();
  }

  /** 橡皮檫关闭 */
  const handleErasureClosed = () => {
    _erasure.current?.reset();
    handleErasureVisible(false);
  }

  /** 橡皮檫确认 */
  const handleErasureConfirm = async () => {
    const result = await _erasure.current?.toErasureURL();
    if (result) {
      images[currentIndex].image = result;
      setImages(cloneDeep(images));
      onClose();
      _erasure.current?.dispose();
      gtag('event', 'event_erasure_confirm');
    }
  }

  /** 顺时针旋转图片 */
  const handleRotate = async () => {
    if (_erasure.current) {
      const image = await loadHTMLImage(currentImage);
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = image.height;
      canvas.height = image.width;
      context?.translate(canvas.width / 2, canvas.height / 2);
      context?.rotate((90 * Math.PI) / 180);
      context?.drawImage(image, -(image.width / 2), -(image.height / 2));
      const rotateSource = canvas.toDataURL();
      setCurrentImage(rotateSource);
      const rotateImage = await loadHTMLImage(rotateSource);
      _erasure.current.changeImage(rotateImage);
    }
  }

  /** 点击完成 */
  const handleSubmit = () => {
    images[currentIndex].image = currentImage;
    setImages(cloneDeep(images));
    onConfirm();
  }

  return (
    <div className={styles.edit}>
      <Header
        className={classnames(styles.header, {
          [styles.headerHide]: erasureVisible
        })}
        closeIconType='left'
        closeIconColor='#000'
        onClose={onClose}
        renderContent={
          <span className={styles.headerContent}>
            {i18n.format('modules.new.sock.edit.avatar')}
          </span>
        }
        renderSubmit={
          <div
            className={styles.headerSubmit}
            onClick={handleSubmit}
          >{i18n.format('modules.global.done')}</div>
        }
      />
      <div className={styles.editContent}>
        <canvas ref={_canvas} />
      </div>
      <div
        className={classnames(styles.erasureButtonGroup, {
          [styles.erasureButtonGroupDown]: erasureVisible
        })}
      >
        {erasureVisible ?
          <Fragment>
            <div className={styles.buttonGroup}>
              <div
                className={styles.erasureClose}
                onClick={handleErasureClosed}
              >
                <Icon type="close" size={30} color="#fff" />
              </div>
              {!revokeDisabled &&
                <div
                  className={styles.erasureRevoke}
                  onClick={() => _erasure.current?.revoke()}
                >
                  <Icon type="revoke" size={26} />
                </div>
              }
            </div>
            <div
              className={styles.erasureConfirm}
              onClick={handleErasureConfirm}
            >
              <Icon type="confirm" size={30} color="#fff" />
            </div>
          </Fragment> :
          <Fragment>
            <div
              className={styles.erasureButton}
              onClick={() => handleErasureVisible(true)}
            >
              <Icon type="erasure" size={30} color="#000" />
            </div>
            <div
              className={styles.rotateButton}
              onClick={() => handleRotate()}
            >
              <Icon type="rotate-right" size={30} color="#000" />
            </div>
          </Fragment>
        }
      </div>
      <div className={classnames(styles.footer, {
        [styles.footerHide]: erasureVisible
      })}>
        <div className={styles.button} onClick={() => setZuploaderVisible(true)}>
          <Icon type='photo' size={30} />
          <span>{i18n.format('modules.global.replace')}</span>
        </div>
        <div className={styles.button} onClick={() => setTaskStatus(SockTaskStatus.Crop)}>
          <Icon type='scissors' size={30} />
          <span>{i18n.format('modules.global.cropper')}</span>
        </div>
        {images.length > 1 &&
          <div className={classnames(styles.button, styles.deleteButton)} onClick={handleDelete}>
            <Icon type='delete-wide' size={30} color='#F56C6C' />
            <span>{i18n.format('modules.global.delete')}</span>
          </div>
        }
      </div>
    </div>
  )
}

export default Edit;
